import { useMemo } from 'react'
import {
  type RetailerCollection,
  RetailerCollectionContentType,
} from 'domains/retailer-collections/utils/types'
import { useGetContentTypeOptions } from 'domains/retailer-collections/components/form/utils/useGetContentTypeOptions.hooks'
import { useRetailerCollectionsContext } from 'domains/retailer-collections/utils/retailerCollections.context'
import { useDomainMessages } from 'domains/retailer-collections/utils/domain/intl'
import { type RetailerCollectionFormMode } from 'domains/retailer-collections/components/form/utils/types'

export const useComputedFormData = ({
  parentRetailerCollection,
  parentRetailerCollectionLevel,
  formMode = 'create',
  numAffectedCollections = 1,
}: {
  parentRetailerCollection?: RetailerCollection
  parentRetailerCollectionLevel?: number
  formMode: RetailerCollectionFormMode
  numAffectedCollections?: number
}) => {
  const config = useRetailerCollectionsContext()

  // If there's a parent, our collection would be its level + 1, otherwise, it's a root collection, level 0
  const proposedCollectionLevel = parentRetailerCollection ? parentRetailerCollectionLevel + 1 : 0

  // Obtain the lowest value available on the currently selected surfaces, this is non zero based.
  // TODO: Surfaces: Use the selected surfaces once it is added on the form
  const combinedMaxDepth = useMemo(() => {
    const maxDepthPerSurfaceValues = parentRetailerCollection?.surfaces.map(
      v => config.maxDepthPerSurface[v]
    ) || [config.maxDepthPerSurface[config.defaultStoreConfiguration.surface]]

    return Math.min(...maxDepthPerSurfaceValues) || 0
  }, [
    parentRetailerCollection,
    config.maxDepthPerSurface,
    config.defaultStoreConfiguration.surface,
  ])

  // Note that combinedMaxDepth is subtracted 1 to account for zero-based calculations here
  // If the current collection level is less but not equal to the combinedMaxDepth, the current collection can hold additional collection type collections
  const canBeCollectionsContentType = proposedCollectionLevel < combinedMaxDepth - 1

  // If the current collection is not root (0) and is lower or equal to the combinedMaxDepth, the current collection can hold product type collections
  const canBeProductsContentType =
    proposedCollectionLevel > 0 && proposedCollectionLevel <= combinedMaxDepth - 1

  const contentTypes = useGetContentTypeOptions({
    [RetailerCollectionContentType.Collections]: {
      disabled: !canBeCollectionsContentType,
    },
    [RetailerCollectionContentType.Products]: {
      disabled: !canBeProductsContentType,
    },
  })

  const messages = useDomainMessages(
    {
      contentTypeRoot: 'retailerCollectionsDomain.form.contentType.collectionOnlyCollection',
      contentTypeMiddle:
        'retailerCollectionsDomain.form.contentType.productOrCollectionsCollection',
      contentTypeLeaf: 'retailerCollectionsDomain.form.contentType.productOnlyCollection',
      contentTypeEditDisabledTooltip:
        'retailerCollectionsDomain.form.contentType.edit.disabledTooltip',
      contentTypeDuplicateDisabledTooltip:
        'retailerCollectionsDomain.form.contentType.duplicate.disabledTooltip',
      contentTypeProductDisabledTooltip:
        'retailerCollectionsDomain.form.contentType.product.disabledTooltip',
      contentTypeCollectionDisabledTooltip:
        'retailerCollectionsDomain.form.contentType.collection.disabledTooltip',
    },
    { collectionLevel: proposedCollectionLevel + 1, numCollections: numAffectedCollections }
  )

  // Change the contentType message based on the collection level being created/updated
  const contentTypeMessage = useMemo(() => {
    if (!parentRetailerCollection) return messages.contentTypeRoot
    if (!canBeCollectionsContentType) return messages.contentTypeLeaf

    return messages.contentTypeMiddle
  }, [parentRetailerCollection, messages, canBeCollectionsContentType])

  // Change the disabled contentType tooltip message based on the collection type or if in edit mode
  const contentTypeDisabledTooltipMessage = useMemo(() => {
    if (formMode === 'edit') return messages.contentTypeEditDisabledTooltip
    if (formMode === 'duplicate') return messages.contentTypeDuplicateDisabledTooltip
    if (!canBeCollectionsContentType) return messages.contentTypeCollectionDisabledTooltip

    return messages.contentTypeProductDisabledTooltip
  }, [messages, canBeCollectionsContentType, formMode])

  const computedMessages = useMemo(
    () => ({ contentTypeMessage, contentTypeDisabledTooltipMessage }),
    [contentTypeDisabledTooltipMessage, contentTypeMessage]
  )

  return {
    combinedMaxDepth,
    canBeCollectionsContentType,
    canBeProductsContentType,
    contentTypes,
    proposedCollectionLevel,
    computedMessages,
  }
}
